import { useState, useMemo, useEffect } from "react";
import { useProjetoContext } from "@/context/projeto";
import { AppContainer } from "@/components/layout/app-container";
import { TabsUnderline } from "@/components/ui/tabs-underline";
import { BankStatementHeader } from "@/components/bank-statement/bank-statement-header";
import { BankStatementTableExtrato } from "@/components/bank-statement/bank-statement-table-extrato";
import { BankStatementSummaryCards } from "@/components/bank-statement/bank-statement-summary-cards";
import { BankAccountSelector } from "@/components/bank-statement/bank-account-selector";
import { ImportStatementDialog } from "@/components/bank-statement/import-statement-dialog";
import { BankStatementFilterDialog } from "@/components/bank-statement/bank-statement-filter-dialog";
import {
  BankStatementTab,
  BankStatementTransaction,
  BankStatementFilters as ApiFilters,
} from "@/types/bank-statement";
import { toast } from "@/hooks/use-toast";
import {
  useBankAccounts,
  useStatements,
  useSummary,
  useImportFile,
  useDeleteRelease,
  useExportTransactions,
  useUpdateRelease,
} from "@/services/queries/useBankStatement";

import { ViewTransactionModal } from "./../demonstrativo-extrato-bancario/components/modals/view-transaction-modal";
import { EditTransactionModal } from "./../demonstrativo-extrato-bancario/components/modals/edit-transaction-modal";
import { DeleteTransactionModal } from "./../demonstrativo-extrato-bancario/components/modals/delete-transaction-modal";
import { LaunchOperationModal } from "./../demonstrativo-extrato-bancario/components/modals/launch-operation-modal";
import { useCreateRelease } from "@/services/queries/useBankStatement";

// Local filter interface for the dialog (different from API filters)
interface LocalFilters {
  status: string[];
  dateFrom: string;
  dateTo: string;
  valueMin: number;
  valueMax: number;
}

export function BankStatement() {
  const { projetoSelecionado } = useProjetoContext();
  const [activeTab, setActiveTab] = useState<BankStatementTab>("Todos");
  const [selectedAccountId, setSelectedAccountId] = useState<number | undefined>(undefined);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState<LocalFilters>({
    status: [],
    dateFrom: "",
    dateTo: "",
    valueMin: 0,
    valueMax: Infinity,
  });

  // Modal States
  const [selectedTransaction, setSelectedTransaction] = useState<BankStatementTransaction | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isLaunchModalOpen, setIsLaunchModalOpen] = useState(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Build API filters based on local filters and active tab
  const apiFilters = useMemo((): ApiFilters => {
    const filters: ApiFilters = {
      page: currentPage,
      limit: itemsPerPage,
    };

    if (selectedAccountId) {
      filters.accountId = selectedAccountId;
    }

    if (projetoSelecionado?.id) {
      filters.movieId = projetoSelecionado.id;
    }

    // Filter by transaction type based on active tab
    if (activeTab === "Entradas") {
      filters.transactionType = "CREDIT";
    } else if (activeTab === "Saídas") {
      filters.transactionType = "DEBIT";
    }
    // "Incentivos" tab will filter client-side on isIncentiveDeposit

    return filters;
  }, [selectedAccountId, localFilters, activeTab, projetoSelecionado?.id, currentPage]);

  // Fetch data from API
  const { data: bankAccounts, isLoading: isLoadingAccounts } = useBankAccounts();
  const { data: transactions, isLoading: isLoadingTransactions } = useStatements(apiFilters);
  const { data: summary, isLoading: isLoadingSummary } = useSummary(selectedAccountId, projetoSelecionado?.id);

  // Mutations
  const importFileMutation = useImportFile();
  const deleteReleaseMutation = useDeleteRelease();
  const exportMutation = useExportTransactions();
  const updateTransactionMutation = useUpdateRelease();
  const createReleaseMutation = useCreateRelease();

  // Set default selected account when accounts load
  useEffect(() => {
    if (bankAccounts?.data && bankAccounts.data.length > 0 && !selectedAccountId) {
      setSelectedAccountId(bankAccounts.data[0].id);
    }
  }, [bankAccounts, selectedAccountId]);

  // Filter transactions for Incentivos tab (client-side)
  const filteredTransactions = useMemo(() => {
    let result = [...transactions?.data || []];

    // For Incentivos tab, filter by isIncentiveDeposit
    if (activeTab === "Incentivos") {
      result = result.filter((t: BankStatementTransaction) => t.isIncentiveDeposit);
    }

    // Apply date range filter (client-side)
    if (localFilters.dateFrom) {
      const fromTimestamp = new Date(localFilters.dateFrom).getTime();
      result = result.filter((t: BankStatementTransaction) => {
        const transDate = new Date(t.transactionDate).getTime();
        return transDate >= fromTimestamp;
      });
    }

    if (localFilters.dateTo) {
      const toTimestamp = new Date(localFilters.dateTo).getTime();
      result = result.filter((t: BankStatementTransaction) => {
        const transDate = new Date(t.transactionDate).getTime();
        return transDate <= toTimestamp;
      });
    }

    // Apply status filter (client-side)
    if (localFilters.status.length > 0) {
      result = result.filter((t: BankStatementTransaction) =>
        localFilters.status.includes(t.status)
      );
    }

    // Apply value range filter (client-side since API might not support it)
    if (localFilters.valueMin > 0 || localFilters.valueMax < Infinity) {
      result = result.filter((t: BankStatementTransaction) => {
        return t.amount >= localFilters.valueMin && t.amount <= localFilters.valueMax;
      });
    }

    return result;
  }, [transactions, activeTab, localFilters]);

  // Loading state
  const isLoading = isLoadingAccounts || isLoadingTransactions || isLoadingSummary;

  // Modal handlers
  const handleImportFile = (file: File) => {
    console.log("Importing file with accountId:", selectedAccountId, "movieId:", projetoSelecionado?.id);
    importFileMutation.mutate(
      {
        file,
        accountId: selectedAccountId,
        movieId: projetoSelecionado?.id,
      },
      {
        onSuccess: () => {
          setImportDialogOpen(false);
        },
      }
    );
  };

  const handleExport = () => {
    exportMutation.mutate({
      filters: apiFilters,
      format: "xlsx",
    });
  };

  const handleView = (transaction: BankStatementTransaction) => {
    setSelectedTransaction(transaction);
    setIsViewModalOpen(true);
  };

  const handleEdit = (transaction: BankStatementTransaction) => {
    setSelectedTransaction(transaction);
    setIsEditModalOpen(true);
  };

  const handleDelete = (transaction: BankStatementTransaction) => {
    setSelectedTransaction(transaction);
    setIsDeleteModalOpen(true);
  };

  const onSaveClassification = (newCategory: string) => {
    if (selectedTransaction) {
      updateTransactionMutation.mutate(
        {
          id: selectedTransaction.id,
          data: { category: newCategory },
        },
        {
          onSuccess: () => {
             setIsEditModalOpen(false);
             toast({
               title: "Sucesso",
               description: "Classificação salva com sucesso.",
             });
          }
        }
      );
    }
  };

  const onConfirmDelete = () => {
    if (selectedTransaction) {
      deleteReleaseMutation.mutate(selectedTransaction.id, {
        onSuccess: () => {
          setIsDeleteModalOpen(false);
          toast({
            title: "Sucesso",
            description: "Operação excluída com sucesso.",
          });
        }
      });
    }
  };

  const onSaveNewOperation = (data: any) => {
    if (!selectedAccountId) {
      toast({
        title: "Erro",
        description: "Selecione uma conta bancária primeiro.",
        variant: "destructive",
      });
      return;
    }

    const activeBankStatementId = transactions?.data && transactions.data.length > 0 ? transactions.data[0].bankStatementId : undefined;

    const payload: any = {
      ...data,
    };

    if (activeBankStatementId) {
      payload.bankStatementId = activeBankStatementId;
    } else {
      payload.accountId = selectedAccountId;
      payload.movieId = projetoSelecionado?.id;
    }

    createReleaseMutation.mutate(payload, {
      onSuccess: () => {
        setIsLaunchModalOpen(false);
      }
    });
  };

  const handleApplyFilters = (newFilters: LocalFilters) => {
    setLocalFilters(newFilters);
    setCurrentPage(1);
    setFilterDialogOpen(false);
    toast({
      title: "Filtros aplicados",
      description: "Os filtros foram aplicados com sucesso.",
    });
  };

  return (
    <AppContainer
      title="Demonstrativo de Extrato Bancário"
      description="Gerencie seus extratos bancários e transações"
      button={
        <div className="flex items-center gap-4">
          {isLoadingAccounts ? (
            <div className="animate-pulse bg-[#15203c] rounded-lg h-14 w-72" />
          ) : bankAccounts?.data.length === 0 ? (
            <div className="bg-[#15203c] rounded-lg h-14 w-72 flex items-center justify-center text-gray-400 text-sm">
              Nenhuma conta bancária
            </div>
          ) : (
            <BankAccountSelector
              accounts={bankAccounts?.data || []}
              selectedAccountId={selectedAccountId?.toString() || ""}
              onAccountChange={(id) => setSelectedAccountId(Number(id))}
            />
          )}

          <BankStatementHeader
            onImportClick={() => setImportDialogOpen(true)}
            onExportClick={handleExport}
          />
        </div>
      }
    >
      <div className="space-y-4 pb-4 mt-2">
        {/* Tabs */}
        <div>
          <TabsUnderline
            tabs={["Todos", "Entradas", "Saídas", "Incentivos"]}
            activeTab={activeTab}
            onTabChange={(tab) => {
              setActiveTab(tab as BankStatementTab);
              setCurrentPage(1);
            }}
          />
        </div>

        {/* Summary Cards */}
        {isLoadingSummary ? (
          <div className="grid grid-cols-4 gap-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="animate-pulse bg-[#15203c] rounded-lg h-20" />
            ))}
          </div>
        ) : (
          <BankStatementSummaryCards
            incentivos={summary?.data?.incentivos ?? 0}
            saldo={summary?.data?.saldo ?? 0}
            entradas={summary?.data?.entradas ?? 0}
            saidas={summary?.data?.saidas ?? 0}
          />
        )}

        {/* Table Content */}
        {isLoadingTransactions ? (
          <div className="animate-pulse bg-darkblue rounded-lg h-80" />
        ) : activeTab === "Incentivos" ? (
          filteredTransactions.length === 0 ? (
            <div className="bg-darkblue rounded-lg p-4 text-white text-center">
              <p className="text-sm">Nenhum depósito de incentivo encontrado.</p>
            </div>
          ) : (
            <BankStatementTableExtrato
              data={filteredTransactions}
              activeTab={activeTab}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onFilterClick={() => setFilterDialogOpen(true)}
              onLaunchOperationClick={() => setIsLaunchModalOpen(true)}
              currentPage={currentPage}
              totalPages={transactions?.pagination?.totalPages || 1}
              totalItems={transactions?.pagination?.total || filteredTransactions.length}
              onPageChange={setCurrentPage}
            />
          )
        ) : (
          <BankStatementTableExtrato
            data={filteredTransactions}
            activeTab={activeTab}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onFilterClick={() => setFilterDialogOpen(true)}
            onLaunchOperationClick={() => setIsLaunchModalOpen(true)}
            currentPage={currentPage}
            totalPages={transactions?.pagination?.totalPages || 1}
            totalItems={transactions?.pagination?.total || filteredTransactions.length}
            onPageChange={setCurrentPage}
          />
        )}
      </div>

      {/* Modals */}
      <ImportStatementDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImport={handleImportFile}
      />

      <BankStatementFilterDialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        filters={localFilters}
        onApply={handleApplyFilters}
      />

      <ViewTransactionModal 
        isOpen={isViewModalOpen} 
        onClose={() => setIsViewModalOpen(false)} 
        transaction={selectedTransaction} 
      />
      
      <EditTransactionModal 
        isOpen={isEditModalOpen} 
        onClose={() => setIsEditModalOpen(false)} 
        transaction={selectedTransaction}
        onSave={onSaveClassification}
        isSaving={updateTransactionMutation.isPending}
      />

      <DeleteTransactionModal 
        isOpen={isDeleteModalOpen} 
        onClose={() => setIsDeleteModalOpen(false)} 
        transaction={selectedTransaction}
        onConfirm={onConfirmDelete}
        isDeleting={deleteReleaseMutation.isPending}
      />

      <LaunchOperationModal
        isOpen={isLaunchModalOpen}
        onClose={() => setIsLaunchModalOpen(false)}
        onSave={onSaveNewOperation}
        isSaving={createReleaseMutation.isPending}
      />
    </AppContainer>
  );
}

export default BankStatement;
